import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  AUTOMATION_WORKFLOW_CREATED,
  AUTOMATION_WORKFLOW_DELETED,
  AUTOMATION_WORKFLOW_TOGGLED,
  AUTOMATION_WORKFLOW_UPDATED,
} from '../../../../constants/analytics-events';
import { Segment } from '../../../../services/analytics-services/segment';
import { LogService } from '../../../../services/log-service';
import { GuidService } from 'ui-components/services/guid-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import type { Workflow as ApiWorkflow, WorkflowRule, WorkflowPayload } from '../../api/automation-workflow-types';
import { WORKFLOW_RULE_REFERENCE_TYPE, WORKFLOW_STATUS } from '../../api/automation-workflow-types';
import {
  createWorkflow as createWorkflowApi,
  deleteWorkflow as deleteWorkflowApi,
  getWorkflow as getWorkflowApi,
  listWorkflows as listWorkflowsApi,
  updateWorkflow as updateWorkflowApi,
} from '../../api/automation-workflow.api';
import type { Workflow, WorkflowEntityType, WorkflowEventType, WorkflowFormData, WorkflowStep, WorkflowStepType } from '../types';
import { convertFromApiFilter, convertFromApiAction } from '../components/step-editor/from-api';
import { messages } from '../messages';

const STEP_TYPE_ACTION: WorkflowStepType = 'action';
const STEP_TYPE_TRIGGER: WorkflowStepType = 'trigger';

const mapApiRuleToStep = (rule: WorkflowRule): WorkflowStep => ({
  id: GuidService.newGuid(),
  type: STEP_TYPE_ACTION,
  title: rule.automationWorkflowRuleName,
  isConfigured: true,
  apiRuleId: rule.automationWorkflowRuleId,
  filters: (rule.filters ?? []).flatMap(convertFromApiFilter),
  actions: (rule.actions ?? []).map(convertFromApiAction),
});

const mapApiWorkflowToLocal = (apiWorkflow: ApiWorkflow): Workflow => {
  // Only the first trigger is used; the backend supports multiple but we enforce one per workflow.
  const triggerSteps: WorkflowStep[] = (apiWorkflow.triggers ?? []).slice(0, 1).map((t) => ({
    id: t.automationWorkflowTriggerId != null ? t.automationWorkflowTriggerId.toString() : GuidService.newGuid(),
    type: STEP_TYPE_TRIGGER,
    title: t.automationWorkflowTriggerName,
    eventType: t.eventType as WorkflowEventType,
    isConfigured: true,
    apiTriggerId: t.automationWorkflowTriggerId,
    eventFilters:
      t.eventFilter?.criteria.map((c) => ({
        field: c.field,
        operator: c.operator,
        value: c.value != null ? c.value.toString() : '',
        valueType: c.valueType,
      })) ?? [],
  }));

  const ruleSteps: WorkflowStep[] = (apiWorkflow.rules ?? []).map(mapApiRuleToStep);

  return {
    id: apiWorkflow.automationWorkflowId != null ? apiWorkflow.automationWorkflowId.toString() : GuidService.newGuid(),
    name: apiWorkflow.automationWorkflowName,
    description: apiWorkflow.description,
    isActive: apiWorkflow.automationWorkflowStatus === WORKFLOW_STATUS.published,
    automationWorkflowStatus: apiWorkflow.automationWorkflowStatus,
    createdAtUtc: apiWorkflow.createdUtc,
    updatedAtUtc: apiWorkflow.modifiedUtc,
    steps: [...triggerSteps, ...ruleSteps],
    entityType: apiWorkflow.entityType as WorkflowEntityType,
  };
};

const stepsToApiPayload = (steps: WorkflowStep[], isActive: boolean): Pick<WorkflowPayload, 'rules' | 'triggers'> =>
  steps.reduce<Pick<WorkflowPayload, 'rules' | 'triggers'>>(
    (acc, s) => {
      if (s.type === STEP_TYPE_ACTION && s.apiRuleId !== undefined) {
        acc.rules.push({
          type: WORKFLOW_RULE_REFERENCE_TYPE.workflowRule,
          definition: { id: s.apiRuleId, status: WORKFLOW_STATUS.published },
        });
      } else if (s.type === STEP_TYPE_TRIGGER) {
        acc.triggers.push({
          ...(s.apiTriggerId !== undefined && { automationWorkflowTriggerId: s.apiTriggerId }),
          automationWorkflowTriggerName: s.title,
          eventType: s.eventType ?? '',
          isActive,
          eventFilter:
            s.eventFilters && s.eventFilters.length > 0
              ? {
                  criteria: s.eventFilters.map((f) => ({
                    field: f.field,
                    operator: f.operator,
                    value: f.value,
                    valueType: f.valueType,
                  })),
                }
              : null,
        });
      }
      return acc;
    },
    { rules: [], triggers: [] },
  );

const formDataToApiPayload = (formData: WorkflowFormData): WorkflowPayload => ({
  automationWorkflowName: formData.name,
  description: formData.description,
  entityType: formData.entityType,
  ...stepsToApiPayload(formData.steps, formData.isActive),
  automationWorkflowStatus: formData.isActive ? WORKFLOW_STATUS.published : WORKFLOW_STATUS.draft,
});

const workflowToApiPayload = (workflow: Workflow): WorkflowPayload => ({
  automationWorkflowName: workflow.name,
  description: workflow.description,
  entityType: workflow.entityType,
  ...stepsToApiPayload(workflow.steps, workflow.isActive),
  automationWorkflowStatus: workflow.automationWorkflowStatus,
});

export const useWorkflows = () => {
  const intl = useIntl();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    listWorkflowsApi()
      .then((apiWorkflows) => {
        const mapped = apiWorkflows.map(mapApiWorkflowToLocal);
        setWorkflows(mapped);
      })
      .catch((err) => {
        LogService.logError(new Error('Failed to load workflows', { cause: err }));
        setIsError(true);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const sortedWorkflows = useMemo(
    () =>
      [...workflows].sort((a, b) => {
        if (a.isActive !== b.isActive) {
          return a.isActive ? -1 : 1;
        }
        return b.updatedAtUtc.localeCompare(a.updatedAtUtc);
      }),
    [workflows],
  );

  const addWorkflow = useCallback(
    (formData: WorkflowFormData) => {
      const payload = formDataToApiPayload(formData);
      return createWorkflowApi(payload)
        .then((result) => {
          const newWorkflowStatus = formData.isActive ? WORKFLOW_STATUS.published : WORKFLOW_STATUS.draft;
          const workflowId = result.automationWorkflowId != null ? result.automationWorkflowId.toString() : GuidService.newGuid();
          // Fetch the full workflow only to backfill apiTriggerId on trigger steps.
          // Without this, a subsequent save omits automationWorkflowTriggerId and the
          // backend creates a second trigger instead of updating the existing one.
          // FIXME: It's not great that we have to make an additional request here. Let's
          // consider returning the full workflow from the create endpoint in the future to avoid this.
          return getWorkflowApi(result.automationWorkflowId).then((fullWorkflow) => {
            const apiTrigger = (fullWorkflow.triggers ?? [])[0];
            const stepsWithTriggerIds = formData.steps.map((s) =>
              s.type === 'trigger' && apiTrigger?.automationWorkflowTriggerId != null
                ? { ...s, apiTriggerId: apiTrigger.automationWorkflowTriggerId }
                : s,
            );
            const newWorkflow: Workflow = {
              id: workflowId,
              name: formData.name,
              description: formData.description,
              isActive: formData.isActive,
              automationWorkflowStatus: newWorkflowStatus,
              createdAtUtc: result.createdUtc,
              updatedAtUtc: result.modifiedUtc,
              steps: stepsWithTriggerIds,
              entityType: formData.entityType,
            };
            setWorkflows((prev) => [...prev, newWorkflow]);
            Segment.trackEvent(AUTOMATION_WORKFLOW_CREATED, {
              workflowId,
              workflowName: formData.name,
              workflowStatus: newWorkflowStatus,
              entityType: formData.entityType,
              stepCount: formData.steps.length,
            });
          });
        })
        .catch((err) => {
          LogService.logError(
            new Error('Failed to create automation workflow', { cause: err }),
            { workflowName: formData.name, entityType: formData.entityType },
            { tags: { featureName: 'automationWorkflows', operation: 'createWorkflow' } },
          );
          NotificationService.showNotification({
            content: intl.formatMessage(messages.saveWorkflowError),
            notificationType: NOTIFICATION_TYPE_ERROR,
          });
          throw err;
        });
    },
    [intl],
  );

  const updateWorkflow = useCallback(
    (id: string, formData: WorkflowFormData) => {
      const previous = workflows.find((w) => w.id === id);
      const updatedStatus = formData.isActive ? WORKFLOW_STATUS.published : WORKFLOW_STATUS.draft;
      setWorkflows((prev) =>
        prev.map((w) =>
          w.id === id
            ? {
                ...w,
                name: formData.name,
                description: formData.description,
                isActive: formData.isActive,
                steps: formData.steps,
                entityType: formData.entityType,
                automationWorkflowStatus: updatedStatus,
              }
            : w,
        ),
      );
      const workflowId = parseInt(id, 10);
      return updateWorkflowApi(workflowId, formDataToApiPayload(formData))
        .then(() => {
          Segment.trackEvent(AUTOMATION_WORKFLOW_UPDATED, {
            workflowId: id,
            workflowName: formData.name,
            workflowStatus: updatedStatus,
            entityType: formData.entityType,
          });
        })
        .catch((err) => {
          LogService.logError(
            new Error('Failed to update automation workflow', { cause: err }),
            { workflowId: id, workflowName: formData.name, entityType: formData.entityType },
            { tags: { featureName: 'automationWorkflows', operation: 'updateWorkflow' } },
          );
          if (previous) {
            setWorkflows((prev) => prev.map((w) => (w.id === id ? previous : w)));
          }
          NotificationService.showNotification({
            content: intl.formatMessage(messages.updateWorkflowError),
            notificationType: NOTIFICATION_TYPE_ERROR,
          });
          throw err;
        });
    },
    [intl, workflows],
  );

  const deleteWorkflow = useCallback(
    (id: string) => {
      const previous = workflows.find((w) => w.id === id);
      setWorkflows((prev) => prev.filter((w) => w.id !== id));
      return deleteWorkflowApi(parseInt(id, 10))
        .then(() => {
          Segment.trackEvent(AUTOMATION_WORKFLOW_DELETED, {
            workflowId: id,
            workflowName: previous?.name ?? '',
            workflowStatus: previous?.automationWorkflowStatus,
          });
        })
        .catch((err) => {
          LogService.logError(
            new Error('Failed to delete automation workflow', { cause: err }),
            { workflowId: id, workflowName: previous?.name },
            { tags: { featureName: 'automationWorkflows', operation: 'deleteWorkflow' } },
          );
          if (previous) {
            setWorkflows((prev) => [...prev, previous]);
          }
          NotificationService.showNotification({
            content: intl.formatMessage(messages.deleteWorkflowError),
            notificationType: NOTIFICATION_TYPE_ERROR,
          });
        });
    },
    [intl, workflows],
  );

  const toggleWorkflowActive = useCallback(
    (id: string) => {
      const previous = workflows.find((w) => w.id === id);
      if (!previous) {
        return;
      }
      const toggledStatus = previous.isActive ? WORKFLOW_STATUS.draft : WORKFLOW_STATUS.published;
      const toggled = { ...previous, isActive: !previous.isActive, automationWorkflowStatus: toggledStatus };
      setWorkflows((prev) => prev.map((w) => (w.id === id ? toggled : w)));
      updateWorkflowApi(parseInt(id, 10), workflowToApiPayload(toggled))
        .then(() => {
          Segment.trackEvent(AUTOMATION_WORKFLOW_TOGGLED, {
            workflowId: id,
            workflowName: toggled.name,
            workflowStatus: toggled.automationWorkflowStatus,
          });
        })
        .catch((err) => {
          LogService.logError(
            new Error('Failed to toggle automation workflow status', { cause: err }),
            { workflowId: id, workflowName: previous.name, attemptedIsActive: toggled.isActive },
            { tags: { featureName: 'automationWorkflows', operation: 'toggleWorkflowStatus' } },
          );
          setWorkflows((prev) => prev.map((w) => (w.id === id ? previous : w)));
          NotificationService.showNotification({
            content: intl.formatMessage(messages.toggleWorkflowError),
            notificationType: NOTIFICATION_TYPE_ERROR,
          });
        });
    },
    [intl, workflows],
  );

  return {
    sortedWorkflows,
    isLoading,
    isError,
    addWorkflow,
    updateWorkflow,
    deleteWorkflow,
    toggleWorkflowActive,
  };
};
